import { useMutation, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { errorMessage, useTRPC } from "../../lib/api";
import { useMe } from "../../lib/me";
import { Button, ErrorText, PageHeader, Section, StatusText, Switch, TextField } from "../../ui";
import { MembersSection } from "./MembersSection";
import { useRefreshWorkspace } from "./refresh";
import { WorkspaceInvitations } from "./WorkspaceInvitations";

export function WorkspaceDetailPage({ workspaceId }: { workspaceId: string }) {
  const trpc = useTRPC();
  const me = useMe();
  const ws = useQuery(trpc.workspaces.get.queryOptions({ workspaceId }));
  const mine = me.workspaces.find((w) => w.id === workspaceId);
  const isOwner = mine?.role === "owner";

  const back = (
    <Link to="/settings/workspaces" className="text-sm text-accent underline-offset-2 hover:underline">
      All workspaces
    </Link>
  );

  if (ws.isPending) return <p className="text-sm text-muted">Loading…</p>;
  if (ws.isError) {
    return (
      <div className="flex flex-col gap-3">
        <ErrorText>{errorMessage(ws.error)}</ErrorText>
        {back}
      </div>
    );
  }

  const personal = ws.data.name === "Personal";
  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={ws.data.name}>{back}</PageHeader>
      <Section title="General">
        <div className="flex flex-col gap-5">
          {isOwner && !personal ? <RenameForm workspaceId={workspaceId} current={ws.data.name} /> : null}
          <AiSetting workspaceId={workspaceId} enabled={ws.data.settings.aiEnabled} canEdit={isOwner} />
        </div>
      </Section>
      <MembersSection workspaceId={workspaceId} isOwner={isOwner} personal={personal} myUid={me.uid} />
      {personal ? null : <WorkspaceInvitations workspaceId={workspaceId} />}
    </div>
  );
}

function RenameForm({ workspaceId, current }: { workspaceId: string; current: string }) {
  const trpc = useTRPC();
  const refresh = useRefreshWorkspace(workspaceId);
  const [name, setName] = useState(current);
  const [saved, setSaved] = useState(false);
  const rename = useMutation(
    trpc.workspaces.updateSettings.mutationOptions({
      onSuccess: async () => {
        setSaved(true);
        await refresh();
      },
    }),
  );

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSaved(false);
    rename.mutate({ workspaceId, name: name.trim() });
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-2">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <TextField
          label="Name"
          name="name"
          required
          maxLength={60}
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="flex-1"
        />
        <Button type="submit" disabled={rename.isPending || name.trim() === current || name.trim().length === 0}>
          {rename.isPending ? "Saving…" : "Rename"}
        </Button>
      </div>
      <div className="min-h-5">
        {rename.isError ? <ErrorText>{errorMessage(rename.error)}</ErrorText> : null}
        {saved ? <StatusText>Renamed.</StatusText> : null}
      </div>
    </form>
  );
}

function AiSetting({ workspaceId, enabled, canEdit }: { workspaceId: string; enabled: boolean; canEdit: boolean }) {
  const trpc = useTRPC();
  const refresh = useRefreshWorkspace(workspaceId);
  const update = useMutation(trpc.workspaces.updateSettings.mutationOptions({ onSuccess: () => refresh() }));
  return (
    <div className="flex flex-col gap-1">
      <Switch
        label="AI enabled"
        checked={update.isPending && update.variables.aiEnabled !== undefined ? update.variables.aiEnabled : enabled}
        disabled={!canEdit || update.isPending}
        onChange={(next) => update.mutate({ workspaceId, aiEnabled: next })}
      />
      <p className="text-xs text-muted">
        Lets Claude read and act on this workspace’s mail.{canEdit ? null : " Only the owner can change this."}
      </p>
      {update.isError ? <ErrorText>{errorMessage(update.error)}</ErrorText> : null}
    </div>
  );
}
